import { FormikErrors } from 'formik';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { FormControl } from '@@/form-components/FormControl';
import { FormError } from '@@/form-components/FormError';
import { FormSection } from '@@/form-components/FormSection';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Slider } from '@@/form-components/Slider';
import { SwitchField } from '@@/form-components/SwitchField';
import { TextTip } from '@@/Tip/TextTip';
import { SliderWithInput } from '@@/form-components/Slider/SliderWithInput';

import { useClusterResourceLimitsQuery } from '../../../queries/useResourceLimitsQuery';

import { NamespaceResourceReservation } from './NamespaceResourceReservation';
import { ResourceQuotaFormValues } from './types';

interface Props {
  values: ResourceQuotaFormValues;
  onChange: (value: ResourceQuotaFormValues) => void;
  enableResourceOverCommit?: boolean;
  errors?: FormikErrors<ResourceQuotaFormValues>;
  namespaceName?: string;
  isEdit?: boolean;
  isEditingDisabled?: boolean;
}

export function ResourceQuotaFormSection({
  values,
  onChange,
  errors,
  isEdit,
  enableResourceOverCommit,
  namespaceName,
  isEditingDisabled,
}: Props) {
  const environmentId = useEnvironmentId();
  const resourceLimitsQuery = useClusterResourceLimitsQuery(environmentId);
  const cpuLimit = resourceLimitsQuery.data?.CPU ?? 0;
  const memoryLimit = resourceLimitsQuery.data?.Memory ?? 0;

  // Handle memory request change
  function handleMemoryRequestChange(value: number) {
    onChange({ ...values, memoryRequest: String(value) });
  }

  // Handle memory limit change
  function handleMemoryLimitChange(value: number) {
    onChange({ ...values, memoryLimit: String(value) });
  }

  // Handle CPU request change
  function handleCpuRequestChange(value: number | number[]) {
    if (Array.isArray(value)) {
      return;
    }
    onChange({ ...values, cpuRequest: String(value) });
  }

  // Handle CPU limit change
  function handleCpuLimitChange(value: number | number[]) {
    if (Array.isArray(value)) {
      return;
    }
    onChange({ ...values, cpuLimit: String(value) });
  }

  return (
    <FormSection title="Resource quota">
      {!isEditingDisabled && (
        <>
          <TextTip color="blue" className="mb-2">
            A resource quota sets boundaries on the compute resources a
            namespace can use. It&apos;s good practice to set a quota for a
            namespace to manage resources effectively. Alternatively, you can
            disable assigning a quota for unrestricted access (not recommended).
          </TextTip>

          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                data-cy="k8sNamespaceCreate-resourceAssignmentToggle"
                disabled={!enableResourceOverCommit}
                label="Resource assignment"
                labelClass="col-sm-3 col-lg-2"
                checked={values.enabled || !enableResourceOverCommit}
                onChange={(enabled) => onChange({ ...values, enabled })}
              />
            </div>
          </div>
        </>
      )}

      {(values.enabled || !enableResourceOverCommit) && !isEditingDisabled && (
        <div>
          <FormSectionTitle>Set resource limits</FormSectionTitle>
          {(!cpuLimit || !memoryLimit) && (
            <FormError>
              Not enough resources available in the cluster to apply a resource
              reservation.
            </FormError>
          )}

          <FormControl
            label="Memory request (MB)"
            inputId="memory-request"
            className="[&>label]:mt-8"
            errors={errors?.memoryRequest}
          >
            {memoryLimit >= 0 && (
              <SliderWithInput
                value={Number(values.memoryRequest) ?? 0}
                onChange={handleMemoryRequestChange}
                max={memoryLimit}
                min={0}
                step={128}
                dataCy="k8sNamespaceCreate-memoryRequest"
                visibleTooltip
                inputId="memory-request"
              />
            )}
          </FormControl>

          <FormControl
            label="Memory limit (MB)"
            inputId="memory-limit"
            className="[&>label]:mt-8"
            errors={errors?.memoryLimit}
          >
            {memoryLimit >= 0 && (
              <SliderWithInput
                value={Number(values.memoryLimit) ?? 0}
                onChange={handleMemoryLimitChange}
                max={memoryLimit}
                min={0}
                step={128}
                dataCy="k8sNamespaceCreate-memoryLimit"
                visibleTooltip
                inputId="memory-limit"
              />
            )}
          </FormControl>

          <FormControl
            label="CPU request"
            inputId="cpu-request"
            className="[&>label]:mt-8"
            errors={errors?.cpuRequest}
          >
            {cpuLimit >= 0 && (
              <Slider
                min={0}
                max={cpuLimit / 1000}
                step={0.1}
                value={Number(values.cpuRequest) ?? 0}
                onChange={handleCpuRequestChange}
                dataCy="k8sNamespaceCreate-cpuRequestSlider"
                visibleTooltip
              />
            )}
          </FormControl>

          <FormControl
            label="CPU limit"
            inputId="cpu-limit"
            className="[&>label]:mt-8"
            errors={errors?.cpuLimit}
          >
            {cpuLimit >= 0 && (
              <Slider
                min={0}
                max={cpuLimit / 1000}
                step={0.1}
                value={Number(values.cpuLimit) ?? 0}
                onChange={handleCpuLimitChange}
                dataCy="k8sNamespaceCreate-cpuLimitSlider"
                visibleTooltip
              />
            )}
          </FormControl>

          {cpuLimit && memoryLimit && typeof errors === 'string' ? (
            <FormError>{errors}</FormError>
          ) : null}
        </div>
      )}
      {namespaceName && isEdit && (
        <NamespaceResourceReservation
          namespaceName={namespaceName}
          environmentId={environmentId}
          resourceQuotaValues={values}
        />
      )}
    </FormSection>
  );
}
